import * as React from "react";
import { Tabs, Tab } from 'react-bootstrap';
import { DataItemConfiguration, WebPortalClient } from "webportalclient";
import { StudyCustomisationModal, StudyCustomisationModalFormValues, FormApiFullDefinition } from "./StudyCustomisationModal";
import { Form, useArrayField, useFormApi, useFormState } from "informedv4";
import { AuditForm, AuditModal } from "../../auditmodal/components/AuditModal";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { UnitType } from "webportalclient";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { Link, useParams } from "react-router-dom";
import { ActionButtons } from "../../misc/ActionButtons";
import { CustomisationTab, StudyCustomisationFormValues } from "./Customisation/CustomisationTab";
import { useCookieRedirect } from "../../misc/CookieRedirect";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";

export const StudyCustomisationScreenForm = ({ formats, importances, hasSites }) => {
    const formApi = useFormApi();

    const client = useWebPortalApiClient();
    const { study } = useCurrentStudyConfigurationContext();
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [submitting, setSubmitting] = React.useState(false);
    const [successAlert, setSuccessAlert] = React.useState(false);
    const [auditModalVisible, setAuditModalVisible] = React.useState(false);

    const onPreAuditSubmit = () => {
        setErrorAlert(null);
        setSuccessAlert(false);
        setSubmitting(true);
        formApi.validate();
        const state = formApi.getFormState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        setSubmitting(false);

        setAuditModalVisible(true);
    }

    const onSubmit = async (auditForm: AuditForm) => {
        setSubmitting(true);
        setSuccessAlert(false);
        setErrorAlert(null);

        const submitItem = async (i: number, dataItem: StudyCustomisationModalFormValues, arrayItem: string) => {
            const itemId = formApi.getInitialValue(`${arrayItem}[${i}].itemId`);

            let dataItemModel = {
                studyId: study.id,
                configurableDataItemId: itemId,
                displayText: dataItem.displayText,
                formatId: dataItem.format?.id,
                customFormat: dataItem.customFormat,
                importanceId: dataItem.importance?.id,
                unitTypeId: dataItem.unitType?.id,
                minimumLength: dataItem.minimumLength,
                maximumLength: dataItem.maximumLength,
                minimumValue: dataItem.minimumValue,
                maximumValue: dataItem.maximumValue,
                prefixWithSiteNumber: dataItem.prefixWithSite,
                prefixDelimiter: dataItem.prefixDelimiter?? null,
            }

            const existingDataItem = await client.client.getDataItemConfigurations(undefined, `studyId eq ${study.id} and configurableDataItemId eq '${dataItemModel.configurableDataItemId}'`);

            if (existingDataItem.value.length > 0) {
                await client.client.patchDataItemConfiguration(dataItemModel, existingDataItem.value[0].id, auditForm.Annotation);
            } else {
                await client.client.postDataItemConfiguration(dataItemModel as DataItemConfiguration);
            }
        }

        const state = formApi.getFormState();

        try {
            for (const [i, dataItem] of (state.values.siteItems as any).entries()) {
                await submitItem(i, dataItem, "siteItems");
            }

            for (const [i, dataItem] of (state.values.subjectItems as any).entries()) {
                await submitItem(i, dataItem, "subjectItems");
            }

            setSuccessAlert(true);
        } catch (ex) {
            setErrorAlert(ex.message);
        }

        setAuditModalVisible(false);
        setSubmitting(false);
    }

    return (
        <>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <Alert show={successAlert} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/studies`}>Studies list</Link>?
            </Alert>
            <div className="row">
                <div className="col-md-12">
                    <h2 className="text-primary">Study Customisation</h2>
                </div>
            </div>
            <div className="panel panel-default" data-automationid="study-customisation-panel">
                <div className="panel-body">
                    {hasSites ? <div className="row">
                        <div className="col-md-12">
                            <Alert show={true} category={AlertCategoryEnum.Warning}>
                                This study contains data, any changes may have unforeseen consequences.
                            </Alert>
                        </div>
                    </div> : null}
                    <div className="row">
                        <Tabs defaultActiveKey={1} id="customisationTabs" className="col-md-12">
                            <Tab key={1} eventKey={1} title="Site">
                                <CustomisationTab arrayFieldName="siteItems" formats={formats} importances={importances} />
                            </Tab>
                            <Tab key={2} eventKey={2} title="Participant">
                                <CustomisationTab arrayFieldName="subjectItems" formats={formats} importances={importances} />
                            </Tab>
                        </Tabs>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <ActionButtons submitting={submitting} onSaveClick={onPreAuditSubmit} automationIdPrefix="ActionsHeader" returnToListRoute="/studies" />
                </div>
            </div>
            {auditModalVisible ? (
                 <AuditModal
                    header="Approval"
                    isSubmitting={submitting}
                    onClose={() => setAuditModalVisible(false)}
                    onSubmit={(audit) => onSubmit(audit)}
                />
            ) : null}
        </>
    );
}

export const StudyCustomisationScreen = () => {
    const client = useWebPortalApiClient();
    const { id } = useParams<{ id: string }>();
    const [formats, setFormats] = React.useState([]);
    const [unitTypes, setUnitTypes] = React.useState<UnitType[]>([]);
    const [importances, setImportances] = React.useState([]);
    const [formInitialValues, setFormInitialValues] = React.useState<StudyCustomisationFormValues>();
    const [hasSites, setHasSites] = React.useState(false);

    useCookieRedirect(id);

    React.useEffect(() => {
        const getData = async () => {
            const siteResult = await client.client.getSites("id", undefined, undefined, undefined, undefined, undefined, undefined, true);
            setHasSites(siteResult["@odata.count"] > 0);

            const refDataItems = await client.client.getConfigurableDataItems();

            const dataItems = await client.client.getDataItemConfigurations(undefined, `studyId eq ${id}`);

            const formatsResult = ["Numeric", "Alphanumeric", "Custom"];
            const formatsMapped = formatsResult.map((x) => (({ name: x, id: x.toUpperCase() })));
            setFormats(formatsMapped);

            const importanceResult = [{ name: "Mandatory", id: "MANDATORY" }, { name: "Optional", id: "OPTIONAL" }, { name: "Not Applicable", id: "NOT_APPLICABLE" }];
            setImportances(importanceResult);

            const unitTypeResult = await client.client.getUnitTypes();
            setUnitTypes(unitTypeResult.value);

            const formValues = {
                siteItems: refDataItems.value.filter(x => x.configurableEntityId === "Site").map(ci => {
                    let customItem = dataItems.value.find(di => di.configurableDataItemId === ci.id);

                    return {
                        itemId: ci.id,
                        name: ci.name,
                        displayText: customItem?.displayText ?? ci.defaultDisplayText,
                        format: customItem?.formatId ? formatsMapped.find(i => i.id === customItem?.formatId) : formatsMapped.find(i => i.id === ci.defaultFormatId),
                        customFormat: customItem?.customFormat ?? ci.customFormat,
                        minimumLength: customItem?.minimumLength ?? ci.defaultMinimumLength,
                        maximumLength: customItem?.maximumLength ?? ci.defaultMaximumLength,
                        minimumValue: customItem?.minimumValue ?? ci.defaultMinimumValue,
                        maximumValue: customItem?.maximumValue ?? ci.defaultMaximumValue,
                        isPrefixAvailable: ci.isPrefixWithSiteNumberAttributeAvailable,
                        prefixWithSite: customItem?.prefixWithSiteNumber ?? ci.defaultPrefixWithSiteNumber,
                        prefixDelimiter: customItem?.prefixDelimiter ?? "",
                        unitType: customItem?.unitTypeId ? unitTypeResult.value.find(ut => ut.id === customItem?.unitTypeId) : unitTypeResult.value.find(ut => ut.id === ci.defaultUnitTypeId),
                        importance: customItem?.importanceId ? importanceResult.find(i => i.id === customItem?.importanceId) : importanceResult.find(i => i.id === ci.defaultImportanceId),
                        isFormatAvailable: ci.isFormatAttributeAvailable,
                        isMinAndMaxLengthAvailable: ci.isMinAndMaxLengthAttributesAvailable,
                        isMinAndMaxValueAvailable: ci.isMinAndMaxValueAttributesAvailable,
                        isUnitTypeAvailable: ci.isUnitTypeAttributeAvailable,
                        isImportanceAvailable: ci.isImportanceAttributeAvailable,
                        isDisplayTextAvailable: ci.isDisplayTextAttributeAvailable,
                        absoluteFieldLength: ci.fieldLength
                    };  
                }),
                subjectItems: refDataItems.value.filter(x => x.configurableEntityId === "Subject").map(ci => {
                    let customItem = dataItems.value.find(di => di.configurableDataItemId === ci.id);

                    return {
                        itemId: ci.id,
                        name: ci.name,
                        displayText: customItem?.displayText ?? ci.defaultDisplayText,
                        format: customItem?.formatId ? formatsMapped.find(i => i.id === customItem?.formatId) : formatsMapped.find(i => i.id === ci.defaultFormatId),
                        customFormat: customItem?.customFormat ?? ci.customFormat,
                        minimumLength: customItem?.minimumLength ?? ci.defaultMinimumLength,
                        maximumLength: customItem?.maximumLength ?? ci.defaultMaximumLength,
                        minimumValue: customItem?.minimumValue ?? ci.defaultMinimumValue,
                        maximumValue: customItem?.maximumValue ?? ci.defaultMaximumValue,
                        isPrefixAvailable: ci.isPrefixWithSiteNumberAttributeAvailable,
                        prefixWithSite: customItem?.prefixWithSiteNumber ?? ci.defaultPrefixWithSiteNumber,
                        prefixDelimiter: customItem?.prefixDelimiter ?? "",
                        unitType: customItem?.unitTypeId ? unitTypeResult.value.find(ut => ut.id === customItem?.unitTypeId) : unitTypeResult.value.find(ut => ut.id === ci.defaultUnitTypeId),
                        importance: customItem?.importanceId ? importanceResult.find(i => i.id === customItem?.importanceId) : importanceResult.find(i => i.id === ci.defaultImportanceId),
                        isFormatAvailable: ci.isFormatAttributeAvailable,
                        isMinAndMaxLengthAvailable: ci.isMinAndMaxLengthAttributesAvailable,
                        isMinAndMaxValueAvailable: ci.isMinAndMaxValueAttributesAvailable,
                        isUnitTypeAvailable: ci.isUnitTypeAttributeAvailable,
                        isImportanceAvailable: ci.isImportanceAttributeAvailable,
                        isDisplayTextAvailable: ci.isDisplayTextAttributeAvailable,
                    };
                })
            }
      
            setFormInitialValues(formValues);
        }

        getData();
    }, []);

    if (!formInitialValues)
        return <LoadingWithMessage message="Loading Study Customisation..." />

    return (
        <Form validateOn="submit" id="customisationForm" noValidate initialValues={formInitialValues as unknown as Record<string, unknown>}>
            <StudyCustomisationScreenForm formats={formats} importances={importances} hasSites={hasSites} />
        </Form>
    );
}
